/**
 * Classifies links in an Oliver answer and stamps internal ones with their conversation id.
 * Pure and DOM-free apart from the default origin.
 */

/** Query parameter carrying the conversation a citation was followed from. */
export const OLIVER_CHAT_PARAM = "oliverChat";

const CONVERSATION_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

export function isConversationId(value) {
  return typeof value === "string" && CONVERSATION_ID.test(value);
}

function defaultOrigin() {
  return typeof window !== "undefined" && window.location
    ? window.location.origin
    : "";
}

/** Parses href as a URL, or null for fragments and bare relative paths. */
function parseHref(href, origin) {
  const raw = String(href ?? "").trim();
  if (!raw || raw.startsWith("#")) return null;
  if (!raw.startsWith("/") && !HAS_SCHEME.test(raw)) return null;
  try {
    return new URL(raw, origin || undefined);
  } catch {
    return null;
  }
}

/**
 * `internal` (a confirmed route here) | `external` | `protocol` (tel:, mailto:) |
 * `unknown` (unparseable, or same-origin with no matching route).
 *
 * Only `internal` changes behaviour. Same origin alone isn't enough: with no catch-all
 * route an unroutable path would land on a blank screen, so `isRoutable` must confirm it.
 *
 * @param {(pathname: string) => boolean} [options.isRoutable]
 */
export function classifyLink(
  href,
  { origin = defaultOrigin(), isRoutable = null } = {}
) {
  const url = parseHref(href, origin);
  if (!url) return "unknown";
  if (url.protocol !== "http:" && url.protocol !== "https:") return "protocol";
  if (!origin || url.origin !== origin) return "external";
  if (typeof isRoutable !== "function" || !isRoutable(url.pathname)) {
    return "unknown";
  }
  return "internal";
}

/** Stamps an internal link with its conversation. No-op for non-internal links or a bad id. */
export function withOliverChat(
  href,
  conversationId,
  { origin, isRoutable } = {}
) {
  const base = origin ?? defaultOrigin();
  if (!isConversationId(conversationId)) return href;
  if (classifyLink(href, { origin: base, isRoutable }) !== "internal") {
    return href;
  }

  const url = parseHref(href, base);
  url.searchParams.set(OLIVER_CHAT_PARAM, conversationId);
  return `${url.pathname}${url.search}${url.hash}`;
}

/** Where a link in an answer goes: a page of this app routed in place, stamped with its chat. */
export function routedLink(href, conversationId, { origin, isRoutable } = {}) {
  const base = origin ?? defaultOrigin();
  const internal =
    classifyLink(href, { origin: base, isRoutable }) === "internal";
  return {
    href: internal
      ? withOliverChat(href, conversationId, { origin: base, isRoutable })
      : href,
    internal,
  };
}

/** A routed link's anchor attributes: a page of this app routed in place, any other in a new tab. */
export function linkAttributes({ href, internal }) {
  return internal
    ? { href, "data-oliver-internal": "1" }
    : { href, target: "_blank", rel: "noopener noreferrer" };
}

/** The conversation id a route asks to restore, or null. Validated — it comes from the URL. */
export function readOliverChat(query) {
  const raw = (query || {})[OLIVER_CHAT_PARAM];
  const candidates = Array.isArray(raw) ? raw : [raw];
  return candidates.find(isConversationId) || null;
}

/** The same query without the restore parameter. */
export function stripOliverChat(query) {
  const next = { ...(query || {}) };
  delete next[OLIVER_CHAT_PARAM];
  return next;
}

/**
 * Does this app have a route for `pathname`? Cached by matched route record, not by path,
 * so the cache stays the size of the route table rather than growing per cited record.
 */
const ROUTE_MATCH_CACHE = new Map();

export function routeExists(router, pathname) {
  if (ROUTE_MATCH_CACHE.has(pathname)) return ROUTE_MATCH_CACHE.get(pathname);

  let exists = false;
  let key = pathname;
  try {
    const matched = router.resolve(pathname)?.matched ?? [];
    exists = matched.length > 0;
    // Cache under the route pattern so every id on the same route shares one entry.
    if (exists) key = matched[matched.length - 1].path || pathname;
  } catch {
    exists = false;
  }

  ROUTE_MATCH_CACHE.set(key, exists);
  if (key !== pathname) ROUTE_MATCH_CACHE.set(pathname, exists);
  return exists;
}

/** Location that drops `oliverChat`, or null when it isn't there. The caller navigates. */
export function routeWithoutOliverChat(route) {
  if (route?.query?.[OLIVER_CHAT_PARAM] === undefined) return null;
  return {
    path: route.path,
    query: stripOliverChat(route.query),
    hash: route.hash,
  };
}
